import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type ClipboardScorePolicy,
  type ScoreFreezeReason,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { type ActivityItem, ActivityList } from "@src/appV2/redesign/components/ActivityList";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { differenceInMinutes } from "date-fns";

import { getClockInSuccessDescription } from "../../Timekeeping/TimekeepingActions/utils/getClockInSuccessDescription";

interface TimekeepingActivityListProps {
  arrivedDate?: Date;
  clockInDate?: Date;
  breakStartDate?: Date;
  breakEndDate?: Date;
  workerStage: WorkerStage;
  workerSubstage?: string;
  workplaceTimezone: string;
  workplaceImage?: string;
  workplaceName?: string;
  mandatoryBreakDurationInMinutes: number | undefined;
  shiftStartDate?: Date;
  hasWorkerClockedInOnTime?: boolean;
  isSecondShiftInDouble?: boolean;
  targetArrivalTime?: Date;
  isTimesheetEditDisabled?: boolean;
  shouldEnforceNfcTimesheetBounds?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  shiftBookedAt?: Date;
  policy?: ClipboardScorePolicy;
  isClipboardScoreFreezeEnabled?: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
}

export function TimekeepingActivityList(props: TimekeepingActivityListProps) {
  const {
    arrivedDate,
    clockInDate,
    breakStartDate,
    breakEndDate,
    workerStage,
    workerSubstage,
    workplaceTimezone,
    workplaceImage,
    workplaceName,
    mandatoryBreakDurationInMinutes,
    hasWorkerClockedInOnTime = false,
    isSecondShiftInDouble = false,
    targetArrivalTime,
    shiftStartDate,
    shiftBookedAt,
    isTimesheetEditDisabled = false,
    shouldEnforceNfcTimesheetBounds = false,
    isArrivalTimeFeatureEnabled = false,
    policy,
    isClipboardScoreFreezeEnabled = false,
    clipboardScoreFreezeReason = undefined,
  } = props;

  const currentDate = useCurrentDate(1000);

  const showClockInSubtitle =
    isDefined(clockInDate) &&
    differenceInMinutes(currentDate, clockInDate) < 10 &&
    !isDefined(breakStartDate) &&
    !isDefined(breakEndDate);
  const clockInSubtitle = showClockInSubtitle
    ? getClockInSuccessDescription({
        earliestGeofenceEntryTime: arrivedDate,
        clockInTime: clockInDate,
        timezone: workplaceTimezone,
        hasWorkerClockedInOnTime,
        isSecondShiftInDouble,
        isTimesheetEditDisabled,
        shouldEnforceNfcTimesheetBounds,
        isArrivalTimeFeatureEnabled,
        targetArrivalTime,
        shiftStartDate,
        shiftBookedAt,
        policy,
        workerStage,
        workerSubstage,
        isClipboardScoreFreezeEnabled,
        clipboardScoreFreezeReason,
      })
    : undefined;

  const activities: ActivityItem[] = [
    {
      id: "arrived",
      icon: isDefined(workplaceImage)
        ? { type: "image", src: workplaceImage, alt: workplaceName }
        : { type: "icon", iconType: "map-pin" },
      title: "Arrived",
      timestamp: arrivedDate ? formatTimeWithTimezone(arrivedDate, workplaceTimezone, false) : "-",
    },
    {
      id: "clock-in",
      icon: { type: "icon", iconType: "clock" },
      title: "Clock in",
      timestamp: clockInDate ? formatTimeWithTimezone(clockInDate, workplaceTimezone, false) : "-",
      subtitle: clockInSubtitle,
    },
  ];

  if (!isDefined(breakStartDate) && mandatoryBreakDurationInMinutes) {
    activities.push({
      id: "break-reminder",
      icon: { type: "icon", iconType: "break" },
      title: "Break reminder",
      subtitle: `Make sure to take your mandatory ${mandatoryBreakDurationInMinutes}-min break`,
      timestamp: "",
    });
  }

  if (isDefined(breakStartDate)) {
    activities.push({
      id: "break-start",
      icon: { type: "icon", iconType: "break" },
      title: "Break start",
      timestamp: formatTimeWithTimezone(breakStartDate, workplaceTimezone, false),
    });
  }

  if (isDefined(breakEndDate)) {
    activities.push({
      id: "break-end",
      icon: { type: "icon", iconType: "break" },
      title: "Break end",
      timestamp: formatTimeWithTimezone(breakEndDate, workplaceTimezone, false),
    });
  }

  return <ActivityList activities={activities} />;
}
